import axios from "axios";

import apiClient from "./client";

import type { MigrationItem, MigrationStatus, PendingDecision } from "@/lib/org-structure-tree";

export type { MigrationItem, MigrationStatus, PendingDecision };

/** Print de tela guardado para uma linha do log (spec evidencia-de-tela, §6.5). */
export interface MigrationLogScreenshot {
  key: string;
  kind: "error" | "success";
  content_type: string;
  size_bytes: number;
}

export interface MigrationLog {
  /** Id da linha no banco. Linhas que chegam pelo SSE não têm. */
  id?: number;
  seq: number;
  level: "info" | "warn" | "error";
  message: string;
  item_id: string | null;
  created_at: string;
  /** Dado estruturado da linha. A tela só lê `screenshot` e `sap_message`. */
  payload?: {
    screenshot?: MigrationLogScreenshot;
    sap_message?: string;
    /** `"consultant"` nas respostas que a pessoa digitou no terminal. */
    actor?: string;
  } | null;
}

export interface CbcConnection {
  configured: boolean;
  base_url?: string;
  username?: string;
  has_credentials?: boolean;
  last_login_at?: string | null;
  /** O workspace do CBC em que o agente trabalha (nome e caixa do topo do CBC). */
  workspace?: string | null;
}

export async function getCbcConnection(projectId: string): Promise<CbcConnection> {
  const response = await apiClient.get<CbcConnection>(
    `/api/projects/${projectId}/cbc-connection`,
  );
  return response.data;
}

export async function saveCbcConnection(
  projectId: string,
  /** `password` ausente = manter a senha já gravada no cofre. */
  body: CbcConnectionInput,
): Promise<void> {
  await apiClient.put(`/api/projects/${projectId}/cbc-connection`, body);
}

export interface CbcConnectionTestResult {
  success: boolean;
  /** Mensagem em pt-BR já pronta para exibição — o backend a gera, não o i18n. */
  message: string;
}

/**
 * Salva a conexão e executa um login real com a credencial informada, na
 * mesma chamada. O veredito é sobre alcançar o CBC autenticado com essa
 * credencial — nada além disso: sucesso aqui não diz que o cadastro de
 * unidades vai passar.
 */
export async function testCbcConnection(
  projectId: string,
  /** `password` ausente = manter a senha já gravada no cofre. */
  body: CbcConnectionInput,
): Promise<CbcConnectionTestResult> {
  const response = await apiClient.post<CbcConnectionTestResult>(
    `/api/projects/${projectId}/cbc-connection/test`,
    body,
  );
  return response.data;
}

/** Checkbox "Usar o mesmo usuário e senha do CBC" e o login próprio da aba Configuration,
 * de quem está logado. A senha nunca volta; `has_password` diz se há uma guardada. */
export interface ConfigurationLogin {
  use_cbc_credentials: boolean;
  username: string | null;
  has_password: boolean;
}

/** `password` ausente = manter a senha guardada. Marcar o checkbox apaga o login próprio. */
export interface ConfigurationLoginInput {
  use_cbc_credentials: boolean;
  username?: string;
  password?: string;
}

export async function getConfigurationLogin(projectId: string): Promise<ConfigurationLogin> {
  const response = await apiClient.get<ConfigurationLogin>(
    `/api/projects/${projectId}/cbc-connection/configuration-login`,
  );
  return response.data;
}

export async function saveConfigurationLogin(
  projectId: string,
  body: ConfigurationLoginInput,
): Promise<ConfigurationLogin> {
  const response = await apiClient.put<ConfigurationLogin>(
    `/api/projects/${projectId}/cbc-connection/configuration-login`,
    body,
  );
  return response.data;
}

export async function listMigrationItems(
  projectId: string,
): Promise<{ items: MigrationItem[] }> {
  const response = await apiClient.get<{ items: MigrationItem[] }>(
    `/api/projects/${projectId}/config-migration/items`,
    { params: { node: "org_structure" } },
  );
  return response.data;
}

/**
 * Bytes do print de uma linha do log, para exibir no modal — nunca para salvar.
 *
 * O servidor acha a chave do objeto no banco a partir da linha; o cliente não
 * informa caminho nenhum. `signal` deixa o react-query cancelar o download se o
 * modal fechar antes de terminar.
 */
export async function fetchRunLogScreenshot(
  projectId: string,
  runId: string,
  logId: number,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await apiClient.get<Blob>(
    `/api/projects/${projectId}/config-migration/runs/${runId}/logs/${logId}/screenshot`,
    { responseType: "blob", signal },
  );
  return response.data;
}

/** 404 = o print não existe (ou não é deste projeto). Não adianta tentar de novo. */
export function isScreenshotNotFound(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

/**
 * Um nó da floresta no formato do wire (snake_case), como o backend o espera.
 *
 * A floresta vai na requisição porque o nó organizacional não existe como
 * linha no banco — ele é montado em tempo de consulta pela query da árvore, e
 * o backend não a duplica. `unit` é a unidade que o agente vai cadastrar.
 */
export interface RunForestNode {
  item_type: string;
  item_key: string;
  parent_key: string | null;
  // Tipo do pai. `parent_key` sozinho é ambíguo: no ECC o mesmo código
  // numérico existe em vários níveis (7000 é company, company_code e
  // shipping_point ao mesmo tempo), e o backend resolve o pai pelo par.
  parent_type: string | null;
  unit: {
    /** O ID da tela: o editado no Gerenciamento, se houver, senão o da origem. */
    id: string;
    /** O ID da origem (ECC), que o agente tenta primeiro; o editado vem depois. */
    source_id?: string | null;
    name: string;
    unit_type: string;
    country?: string | null;
    city?: string | null;
    local_currency?: string | null;
    subtype?: string | null;
  };
}

/** Credenciais do CBC de quem está logado; `password` ausente mantém a senha já gravada no cofre. */
export interface CbcConnectionInput {
  base_url: string;
  username: string;
  password?: string;
  /** O workspace do CBC (nome e caixa do topo do CBC). Texto grava, vazio apaga e ausente mantém. */
  workspace?: string;
}

